import { useState, useEffect } from 'react';
import { List, Row, Col, Spin, Button, Tooltip, Affix, Switch, notification, Space, Select, Input } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useApplicationsLogic } from '../../useApplicationsLogic';
import CreateApplicationModal from './CreateApplicationModal';
import { useI18nLocalization } from '../../../i18nLocalization/useI18nLocalization';
import EditApplicationModal from './EditApplicationModal';
import PageContent from '../../../structure/PageContent';
import { useCategoriesLogic } from '../../useCategoriesLogic';

const Admin = () => {
	const applicationsLogic = useApplicationsLogic();
	const categoryLogic = useCategoriesLogic();
	const [applications, setApplications] = useState(null);
	const [appNamesList, setAppNamesList] = useState(null);
	const [listData, setListData] = useState(null);
	const { t } = useI18nLocalization();

	const [createApplicationModalIsVisible, setApplicationModalIsVisible] = useState(false);
	const [editApplicationModalIsVisible, setEditApplicationModalIsVisible] = useState(false);
	const [selectedApp, setSelectedApp] = useState(null);
	const [categories, setCategories] = useState(null);

	//filter variables
	const [categoryFilter, setCategoryFilter] = useState(null);
	const [enabledFilter, setEnabledFilter] = useState(null);
	const [visibleFilter, setVisibleFilter] = useState(null);
	const [searchFilter, setSearchFilter] = useState('');

	const { Option } = Select;

	useEffect(() => {
		if (!applications) {
			getApplications();
		}

		if (!categories) {
			getCategories();
		}
	}, []);

	useEffect(() => {
		if (applications) {
			applyFilters();
		}
	}, [categoryFilter, enabledFilter, visibleFilter, searchFilter, JSON.stringify(applications)]);

	useEffect(() => {
		if (applications) {
			const listDataTemp = applications.map((app) => {
				const appInfo = {
					id: app.id,
					name: app.name,
					description: app.description,
					url: app.url,
					supportUrl: app.supportUrl,
					logo: app.logo,
					icon: app.icon,
					show: app.show,
					color: app.color,
					enabled: app.enabled,
					categoryId: app.categoryId
				};

				return appInfo;
			});
			setListData(listDataTemp);
			applyFilters();
		}
	}, [JSON.stringify(applications)]);

	//helper function for async (cant do async in useffect)
	const getApplications = async () => {
		const apps = await applicationsLogic.getApplications();
		setApplications(apps);
		setAppNamesList(
			apps.map((app) => {
				return app.name;
			})
		);
	};

	const getCategories = async () => {
		const cats = await categoryLogic.getCategories();
		setCategories(cats);
	};

	const updateShow = async (id, name, checked) => {
		const updatedAppModel = {
			id: id,
			show: checked
		};

		const successful = await applicationsLogic.updateApplicationVisibility(updatedAppModel);
		if (successful) {
			notification['success']({
				message: checked ? t(`${name} is now visible`) : t(`${name} has been hidden from view`),
				duration: 3
			});
		} else {
			notification['error']({
				message: t(`We encountered an error.`),
				duration: 3
			});
		}
	};

	const getSelectedApp = () => {
		return selectedApp;
	};

	const setCategory = (value) => {
		if (value === undefined) {
			setCategoryFilter(null);
		} else {
			const categorySelection = value;
			setCategoryFilter(categorySelection);
		}
	};

	const setEnabled = (value) => {
		if (value === undefined) {
			setEnabledFilter(null);
		} else {
			const enabledBool = value === 'enabled' ? true : false;
			setEnabledFilter(enabledBool);
		}
	};

	const setVisible = (value) => {
		if (value === undefined) {
			setVisibleFilter(null);
		} else {
			const visibleBool = value === 'visible' ? true : false;
			setVisibleFilter(visibleBool);
		}
	};

	const setSearch = (input) => {
		setSearchFilter(input.target.value);
	};

	const applyFilters = () => {
		let filteredApps = applications.map((app) => {
			return app;
		});
		if (categoryFilter !== null) {
			filteredApps = filteredApps.filter((app) => app.categoryId === categoryFilter);
		}
		if (enabledFilter !== null) {
			filteredApps = filteredApps.filter((app) => app.enabled === enabledFilter);
		}
		if (visibleFilter !== null) {
			filteredApps = filteredApps.filter((app) => app.show === visibleFilter);
		}
		if (searchFilter != '') {
			filteredApps = filteredApps.filter((app) => {
				return app.name.toLowerCase().includes(searchFilter.toLowerCase());
			});
		}
		setListData(filteredApps);
	};

	return !listData || !categories || !appNamesList ? (
		<Row justify='center' align='middle' style={{ height: '100%' }}>
			<Col>
				<Spin />
			</Col>
		</Row>
	) : (
		<>
			<Row gutter={[8, 0]}>
				<Col span={6}>
					<Input placeholder={t('Search')} onChange={setSearch} allowClear></Input>
				</Col>
				<Col span={6}>
					<Select placeholder={t('Category') + '?'} style={{ width: '100%' }} onChange={setCategory} allowClear>
						{_.map(categories, (category) => {
							return (
								<Option value={category.id} key={category.id}>
									{category.name}
								</Option>
							);
						})}
					</Select>
				</Col>
				<Col span={6}>
					<Select
						placeholder={t('Enabled') + ' ' + t('or') + ' ' + t('Disabled') + '?'}
						style={{ width: '100%' }}
						onChange={setEnabled}
						allowClear
					>
						<Option value='enabled'>{t('Enabled')}</Option>
						<Option value='disabled'>{t('Disabled')}</Option>
					</Select>
				</Col>
				<Col span={6}>
					<Select
						placeholder={t('Visible') + ' ' + t('or') + ' ' + t('Hidden') + '?'}
						style={{ width: '100%' }}
						onChange={setVisible}
						allowClear
					>
						<Option value='visible'>{t('Visible')}</Option>
						<Option value='hidden'>{t('Hidden')}</Option>
					</Select>
				</Col>
			</Row>
			<List
				itemLayout='vertical'
				dataSource={listData}
				renderItem={(item) => (
					<PageContent className='margin-top-10-px'>
						<List.Item
							key={item.id}
							extra={[
								<Row key={item.id} align='middle' style={{ height: '100%' }}>
									<Col>
										<img src={item.logo} style={{ float: 'left', maxWidth: '200px', maxHeight: '75px' }} />
									</Col>
								</Row>
							]}
							actions={[
								<Space key={item.id}>
									<Button
										onClick={() => {
											setEditApplicationModalIsVisible(true);
											setSelectedApp(item);
										}}
										type='text'
										icon={<SettingOutlined />}
									></Button>
									{t('Edit')}
								</Space>,
								<Switch
									defaultChecked={item.show}
									checkedChildren={t('Visible')}
									unCheckedChildren={t('Hidden')}
									onChange={(checked) => updateShow(item.id, item.name, checked)}
									key='list-switch'
								/>
							]}
						>
							<List.Item.Meta
								title={item.name}
								description={item.description}
								avatar={<img style={{ width: '30px' }} src={item.icon} />}
							/>
						</List.Item>
					</PageContent>
				)}
			/>
			{getSelectedApp() && (
				<EditApplicationModal
					isVisible={editApplicationModalIsVisible}
					onSaveCallback={() => {
						getApplications();
						setEditApplicationModalIsVisible(false);
						setSelectedApp(null);
						applyFilters();
					}}
					onCloseCallback={() => {
						setEditApplicationModalIsVisible(false);
						setSelectedApp(null);
					}}
					application={selectedApp}
					appNameList={appNamesList}
					categories={categories}
				/>
			)}

			<Affix style={{ position: 'fixed', bottom: '7%', right: '2%' }}>
				<span>
					<Tooltip title={t('Add') + ' ' + t('Application')}>
						<Button
							type='primary'
							shape='circle'
							icon={<PlusOutlined />}
							onClick={() => {
								setApplicationModalIsVisible(true);
							}}
						/>
					</Tooltip>
					<CreateApplicationModal
						isVisible={createApplicationModalIsVisible}
						onSaveCallback={() => {
							getApplications();
							setApplicationModalIsVisible(false);
						}}
						onCloseCallback={() => {
							setApplicationModalIsVisible(false);
						}}
						appNameList={appNamesList}
						categories={categories}
					/>
				</span>
			</Affix>
		</>
	);
};

export default Admin;
